import { useTranslate } from "#src/hooks";
import {
  LaptopOutlined,
  MobileOutlined,
  TabletOutlined,
} from "@ant-design/icons";
import { Flex, Segmented } from "antd";
import { useAntTheme } from "hooks/useAntTheme/";

export type AspectRatioType = "desktop" | "tablet" | "mobile";

const ICON_BY_TYPE: Record<AspectRatioType, React.ReactNode> = {
  desktop: <LaptopOutlined />,
  tablet: <TabletOutlined />,
  mobile: <MobileOutlined />,
};

const LABEL_KEY_BY_TYPE: Record<AspectRatioType, string> = {
  desktop:
    "components.templates.templatePreview.mediaResolutionSegmentedButtons.desktop",
  tablet:
    "components.templates.templatePreview.mediaResolutionSegmentedButtons.tablet",
  mobile:
    "components.templates.templatePreview.mediaResolutionSegmentedButtons.mobile",
};

export type AspectRatioSegmentedOption<T extends string | number> = {
  type: AspectRatioType;
  value: T;
};

export type AspectRatioSegmentedProps<T extends string | number> = {
  options: AspectRatioSegmentedOption<T>[];
  // `value`/`onChange` are injected automatically when rendered inside an
  // AntD `Form.Item`, or can be passed directly for controlled usage.
  value?: T;
  onChange?: (value: T) => void;
  vertical?: boolean;
  showLabel?: boolean;
  block?: boolean;
  style?: React.CSSProperties;
};

export const AspectRatioSegmented = <T extends string | number>({
  options,
  value,
  onChange,
  vertical = false,
  showLabel = true,
  block = false,
  style,
}: AspectRatioSegmentedProps<T>) => {
  const t = useTranslate();
  const { theme } = useAntTheme();

  return (
    <Segmented
      vertical={vertical}
      block={block}
      value={value}
      onChange={(next) => onChange?.(next)}
      options={options.map(({ type, value: optionValue }) => ({
        value: optionValue,
        label: (
          <Flex
            vertical
            align="center"
            justify="center"
            gap={2}
            style={{ padding: theme.paddingXS }}
          >
            {ICON_BY_TYPE[type]}
            {showLabel && t(LABEL_KEY_BY_TYPE[type])}
          </Flex>
        ),
      }))}
      style={{
        backgroundColor: theme.colorBgContainerDisabled,
        ...style,
      }}
    />
  );
};
